// =============================================================================
// API Client Types
// =============================================================================

import type { ApiErrorResponse } from '@tkpod/shared';

/** HTTP methods supported by the client */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Configuration for creating an ApiClient instance */
export interface ApiClientConfig {
  /** Base URL for API requests (e.g., process.env.API_URL) */
  baseUrl: string;

  /**
   * Credentials mode for fetch.
   * 'include' sends cookies cross-origin (required for auth).
   * @default 'include'
   */
  credentials?: RequestCredentials;

  /** Default timeout in milliseconds. @default 30000 */
  timeout?: number;

  /** Maximum number of retries for failed requests. @default 2 */
  maxRetries?: number;

  /** Base delay for exponential backoff in ms. @default 1000 */
  retryBaseDelay?: number;

  /**
   * Callback invoked when a 401 is received.
   * Typically triggers token refresh or redirect to login.
   * Return `true` to retry the original request after handling.
   */
  onUnauthorized?: () => Promise<boolean>;

  /**
   * Called after a successful token refresh (401 → refresh → success).
   * Use to reset proactive-refresh timers or update UI state.
   */
  onTokenRefreshed?: () => void;

  /**
   * Called when refresh fails and user must re-authenticate.
   * Guaranteed to fire only once (debounced across tabs via BroadcastChannel).
   */
  onSessionExpired?: () => void;

  /** Optional request interceptor (e.g., add custom headers) */
  onRequest?: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;

  /** Optional response interceptor (e.g., logging, telemetry) */
  onResponse?: (response: Response, config: RequestConfig) => void | Promise<void>;

  /** Optional error interceptor */
  onError?: (error: ApiClientError) => void;
}

/** Configuration for a single request */
export interface RequestConfig {
  method: HttpMethod;
  path: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
  /** Skip retry logic for this request */
  noRetry?: boolean;
  /** Signal for abort control */
  signal?: AbortSignal;
}

/** Options passed to individual request methods */
export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  timeout?: number;
  noRetry?: boolean;
  signal?: AbortSignal;
}

/** Structured error thrown by the API client */
export class ApiClientError extends Error {
  /** HTTP status code (0 for network errors) */
  public readonly statusCode: number;
  /** Machine-readable error code from the API */
  public readonly errorCode: string;
  /** Correlation ID for tracing */
  public readonly correlationId: string | null;
  /** Full API error response (if available) */
  public readonly apiError: ApiErrorResponse | null;
  /** Whether this error is retryable */
  public readonly retryable: boolean;

  constructor(params: {
    message: string;
    statusCode: number;
    errorCode: string;
    correlationId?: string | null;
    apiError?: ApiErrorResponse | null;
    retryable?: boolean;
  }) {
    super(params.message);
    this.name = 'ApiClientError';
    this.statusCode = params.statusCode;
    this.errorCode = params.errorCode;
    this.correlationId = params.correlationId ?? null;
    this.apiError = params.apiError ?? null;
    this.retryable = params.retryable ?? false;
  }

  /** Check if the error is a specific error code */
  is(code: string): boolean {
    return this.errorCode === code;
  }

  /** Check if the error is a validation error */
  get isValidation(): boolean {
    return this.errorCode === 'VALIDATION_FAILED';
  }

  /** Check if the error is an auth error */
  get isAuth(): boolean {
    return this.errorCode.startsWith('AUTH_');
  }

  /** Check if the error is a network error */
  get isNetwork(): boolean {
    return this.statusCode === 0;
  }

  /** Check if the error is a timeout */
  get isTimeout(): boolean {
    return this.errorCode === 'TIMEOUT';
  }
}
